import { useState } from 'react'
import type { PolicySettings } from '@/lib/apiTypes'
import { useOrgPolicySettings } from '@/lib/hooks'
import { api } from '@/lib/api'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { EmptyState } from '@/shared/components/EmptyState'
import { SkeletonCard } from '@/shared/components/Skeleton'
import { formatDateTime } from '@/lib/format'
import { useToast } from '@/context/ToastContext'
import { PolicyChangeHistory } from '@/features/policies/components/PolicyChangeHistory'
import { ToggleRow } from '@/features/policies/components/ToggleRow'
import { ARTEFACT_ROWS, type ToggleKey } from '@/features/policies/lib/enablementRows'

/**
 * Global + per-artefact policy enablement panel. The source-of-truth toggles for
 * whether policies APPLY (layered above the tier gate + per-policy flags).
 */
export function PolicyEnablementPanel({ orgId, canAdmin }: { orgId: string; canAdmin: boolean }) {
  const { data, loading, error, refetch } = useOrgPolicySettings(orgId)
  const { showToast } = useToast()
  const [busy, setBusy]       = useState<ToggleKey | null>(null)
  const [gateBusy, setGateBusy] = useState(false)
  const [confirm, setConfirm] = useState<{ key: ToggleKey; label: string } | null>(null)

  function note(msg: string, ok = true) {
    showToast(msg, ok ? 'success' : 'error')
  }

  async function setGate(value: 'ignore' | 'hard_fail') {
    if (data?.on_missing_signal === value) return
    setGateBusy(true)
    try {
      await api.patch(`/api/orgs/${orgId}/policies/settings`, { on_missing_signal: value })
      await refetch()
      note(value === 'hard_fail' ? 'Signal fail gate set to Hard Fail.' : 'Signal fail gate set to Ignore.')
    } catch (err) {
      note((err as Error).message, false)
    } finally {
      setGateBusy(false)
    }
  }

  async function apply(key: ToggleKey, value: boolean) {
    setBusy(key)
    try {
      const res = await api.patch<PolicySettings & { changed?: boolean; rows_changed?: number; error?: string }>(
        `/api/orgs/${orgId}/policies/settings`, { [key]: value },
      )
      await refetch()
      if (!value && (res.rows_changed ?? 0) > 0) {
        note(`Disabled. ${res.rows_changed} version${res.rows_changed === 1 ? '' : 's'} unblocked.`)
      } else if (value && (res.rows_changed ?? 0) > 0) {
        note(`Enabled. ${res.rows_changed} version${res.rows_changed === 1 ? '' : 's'} re-blocked by policy.`)
      } else {
        note(value ? 'Enabled.' : 'Disabled.')
      }
    } catch (err) {
      note((err as Error).message, false)
    } finally {
      setBusy(null)
    }
  }

  // Disabling a toggle can immediately unblock artefacts — confirm first, seeded
  // with the advisory blocked counts (display-only; the server recompute is
  // authoritative). Global covers all three artefacts; each artefact toggle its own.
  function blockedParts(key: ToggleKey): string[] {
    const parts: string[] = []
    const add = (n: number, unit: string) => { if (n > 0) parts.push(`${n} ${unit}${n === 1 ? '' : 's'}`) }
    if (key === 'global' || key === 'modules')   add(data?.modules_blocked_count ?? 0, 'module version')
    if (key === 'global' || key === 'state')     add(data?.state_blocked_count ?? 0, 'state workspace')
    if (key === 'global' || key === 'providers') add(data?.providers_blocked_count ?? 0, 'provider version')
    return parts
  }

  function requestToggle(key: ToggleKey, label: string, currentlyOn: boolean) {
    if (currentlyOn && blockedParts(key).length > 0) {
      setConfirm({ key, label })
    } else {
      apply(key, !currentlyOn)
    }
  }

  if (loading) return <SkeletonCard rows={4} />
  if (error || !data) {
    return <EmptyState icon="⚠" title="Failed to load policy settings" subtitle={error ?? undefined} />
  }

  const tierGated = !data.opaTier

  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '16px 18px', display: 'grid', gap: 14 }}>
      {confirm && (
        <ConfirmModal
          track="org_policy_disable_confirm"
          title={`Disable ${confirm.label.toLowerCase()} policies`}
          message={`This will immediately lift policy blocks on ${blockedParts(confirm.key).join(', ')} (downloads, applies and provider files become available again). You can re-enable later to restore blocking. Continue?`}
          confirmLabel="Disable"
          danger
          busy={busy === confirm.key}
          onConfirm={() => { const k = confirm.key; setConfirm(null); apply(k, false) }}
          onCancel={() => setConfirm(null)}
        />
      )}

      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', color: 'var(--text3)', letterSpacing: '0.08em' }}>
          // policy enablement
        </div>
        {(data.last_changed_by || data.last_changed_at) && (
          <div style={{ fontSize: '0.68rem', color: 'var(--text3)' }}>
            last changed{data.last_changed_by ? ` by ${data.last_changed_by}` : ''}{data.last_changed_at ? ` on ${formatDateTime(data.last_changed_at)}` : ''}
          </div>
        )}
      </div>

      {tierGated && (
        <div className="feedback-error" style={{ background: 'transparent', border: '1px solid var(--border)' }}>
          Policy enforcement requires a Business or Enterprise plan. <a href="/billing">View plans</a>.
        </div>
      )}

      {/* Master switch */}
      <ToggleRow
        label="Policies enabled"
        sublabel="Master switch for the entire policy feature."
        on={data.global}
        disabled={!canAdmin || tierGated || busy !== null}
        busy={busy === 'global'}
        onToggle={() => requestToggle('global', 'all', data.global)}
      />

      {/* Per-artefact toggles */}
      {ARTEFACT_ROWS.map((row) => (
        <ToggleRow
          key={row.key}
          label={row.label}
          sublabel={row.sublabel}
          on={data[row.key]}
          disabled={!canAdmin || tierGated || !data.global || busy !== null}
          busy={busy === row.key}
          indent
          onToggle={() => requestToggle(row.key, row.label, data[row.key])}
        />
      ))}

      {/* Signal fail gate — how a missing trust signal is treated */}
      <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, display: 'grid', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '0.85rem', color: 'var(--text)' }}>Signal fail gate</div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text3)', marginTop: 2 }}>
              When a provider's trust signal (stars, licence, activity) can't be determined, decide whether that counts as a failure.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <button
              className={`btn btn-sm ${data.on_missing_signal === 'ignore' ? 'btn-primary' : 'btn-ghost'}`}
              disabled={!canAdmin || tierGated || gateBusy}
              onClick={() => setGate('ignore')}
            >
              Ignore
            </button>
            <button
              className={`btn btn-sm ${data.on_missing_signal === 'hard_fail' ? 'btn-primary' : 'btn-ghost'}`}
              disabled={!canAdmin || tierGated || gateBusy}
              onClick={() => setGate('hard_fail')}
            >
              Hard Fail
            </button>
            {gateBusy && <span className="spinner" />}
          </div>
        </div>
        <div style={{ fontSize: '0.7rem', color: 'var(--text3)' }}>
          {data.on_missing_signal === 'hard_fail'
            ? 'Hard Fail: a missing signal blocks the provider (fail-closed) — safest, but it also flags providers whose source repo can’t be inspected.'
            : 'Ignore: a missing signal is skipped, so only providers with a known-bad signal are flagged (fail-open).'}
        </div>
      </div>

      <PolicyChangeHistory orgId={orgId} />
    </div>
  )
}
